/**
 * AdminDijitalRozet — Dijital Rozet entegrasyonu yönetimi.
 * Route: /admin/dijital-rozet · Roles: admin, superadmin
 *
 * İki sekme:
 *   Kurslar — her kurs için beceri seçimi, tip/seviye, kazanım kriteri ve
 *             Dijital Rozet `Detail` kaydına gönderim.
 *   Kuyruk  — sertifikaların rozet durumu; yayınlama görevi (sweeper) buradan
 *             tetiklenir ve başarısız olanlar yeniden denenir.
 *
 * Yayınlama iki adımlıdır: önce kuru koşu, sonra açık onayla gönderim. Dijital
 * Rozet'in test ortamı yoktur — her gönderim canlı devlet sistemine gider.
 *
 * Kurs filtresi bilinçli olarak YOK: hangi kursun rozetleneceği kurs bazında
 * açılır, böylece yeni kurs eklendiğinde kod değişikliği gerekmez.
 */

import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, RefreshCw, XCircle } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, LoadingState, EmptyState } from '../components/admin';
import DijitalRozetSkillPicker from '../components/admin/DijitalRozetSkillPicker';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../contexts/ToastContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  useDijitalRozetStatus,
  useDijitalRozetCourses,
  useUpdateCourseRozetSettings,
  useSyncCourseDetail,
  useDijitalRozetCertificates,
  useRetryCertificateRozet,
  useRunSweep,
  useDijitalRozetSilmeKuyruguOzeti,
} from '../api/admin/dijitalRozet';

// Detail.type enum — backend services/dijital_rozet_service.py ile birebir
const DETAIL_TIPLERI = [
  { value: 3, label: 'Çevrim İçi Kurs' },
  { value: 2, label: 'Sınıf Kursu' },
  { value: 12, label: 'Hibrit Kurs' },
  { value: 5, label: 'Başarı' },
  { value: 6, label: 'Lisans' },
  { value: 10, label: 'Mezuniyet' },
  { value: 4, label: 'Webinar' },
];

const SEVIYELER = [
  { value: 5, label: 'Belirtilmemiş' },
  { value: 0, label: 'Başlangıç' },
  { value: 1, label: 'Orta' },
  { value: 2, label: 'İleri' },
  { value: 3, label: 'Deneyimli' },
  { value: 4, label: 'Profesyonel' },
];

// backend services/dijital_rozet_sweeper.py durum makinesiyle birebir
const KUYRUK_DURUMLARI = [
  { value: '', label: 'Tümü' },
  { value: 'pending', label: 'Bekliyor' },
  { value: 'publishing', label: 'Uçuşta' },
  { value: 'sent', label: 'Gönderildi' },
  { value: 'failed', label: 'Başarısız' },
  { value: 'skipped', label: 'Atlandı' },
  { value: 'awaiting_consent', label: 'Rıza bekliyor' },
];

const DURUM_ETIKETI = {
  pending: 'bekliyor',
  publishing: 'uçuşta',
  sent: 'gönderildi',
  failed: 'başarısız',
  skipped: 'atlandı',
  awaiting_consent: 'rıza bekliyor',
  revoked: 'iptal',
};

const ATLAMA_SEBEBI = {
  detail_not_ready: 'Kursun Detail kaydı henüz "Ready" değil — beceri/açıklama/süre eksik.',
  detail_not_configured: 'Kursun Dijital Rozet Detail kaydı yok.',
  no_email: 'Öğrencide e-posta adresi yok.',
  consent_missing: 'Öğrenci veri aktarımına rıza vermedi.',
  course_not_found: 'Sertifikanın kursu bulunamadı.',
};

const DURUM_RENGI = {
  sent: 'default',
  pending: 'secondary',
  publishing: 'secondary',
  failed: 'destructive',
  skipped: 'outline',
  awaiting_consent: 'outline',
  revoked: 'outline',
};

export default function AdminDijitalRozet() {
  const [sekme, setSekme] = useState('kurslar');

  return (
    <AdminLayout>
      <PageHeader
        title="Dijital Rozet"
        subtitle="Sanayi ve Teknoloji Bakanlığı Dijital Rozet platformu entegrasyonu"
      />

      <DurumKarti />

      <SilmeKuyruguOzeti />

      <div className="mb-4 mt-6 flex gap-2 border-b">
        {[
          { id: 'kurslar', label: 'Kurslar' },
          { id: 'kuyruk', label: 'Rozet Kuyruğu' },
        ].map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSekme(s.id)}
            className={`px-4 py-2 text-sm ${
              sekme === s.id
                ? 'border-b-2 border-primary font-medium text-primary'
                : 'text-muted-foreground'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sekme === 'kurslar' ? <KursListesi /> : <RozetKuyrugu />}
    </AdminLayout>
  );
}

// ─── Durum kartı ──────────────────────────────────────────────────────────────

function DurumKarti() {
  const [ping, setPing] = useState(false);
  const { data, isLoading } = useDijitalRozetStatus(ping);

  if (isLoading) return <LoadingState />;
  if (!data) return null;

  const satirlar = [
    ['Entegrasyon', data.enabled ? 'Açık' : 'Kapalı', data.enabled],
    ['API anahtarı', data.has_api_key ? 'Tanımlı' : 'Eksik', data.has_api_key],
    ['Tasarım (Design)', data.has_design_id ? 'Tanımlı' : 'Eksik', data.has_design_id],
    ['E-posta şablonu', data.has_email_template_id ? 'Tanımlı' : 'Eksik', data.has_email_template_id],
  ];

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-medium">Bağlantı durumu</h2>
        <Button type="button" size="sm" variant="outline" onClick={() => setPing(true)}>
          Bağlantıyı test et
        </Button>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {satirlar.map(([etiket, deger, ok]) => (
          <div key={etiket} className="flex items-center justify-between rounded-md border px-3 py-2">
            <span className="text-sm text-muted-foreground">{etiket}</span>
            <Badge variant={ok ? 'default' : 'destructive'}>{deger}</Badge>
          </div>
        ))}
      </div>

      {!data.campaign_ready && (
        <p className="mt-3 rounded-md bg-muted p-3 text-sm">
          Tasarım veya e-posta şablonu tanımlı değil. Bu ikisi API&apos;den oluşturulamaz —
          Dijital Rozet panelinden elle üretilip <code>.env</code> dosyasına
          (<code>DIJITAL_ROZET_DESIGN_ID</code>, <code>DIJITAL_ROZET_EMAIL_TEMPLATE_ID</code>)
          yazılmalıdır. Bunlar olmadan rozet yayınlanamaz.
        </p>
      )}

      {data.consent_filter && (
        <p className="mt-3 rounded-md bg-muted p-3 text-sm">
          <strong>KVKK rıza filtresi açık.</strong> Rozet yalnızca kullanıcı kaydında{' '}
          <code>{data.consent_field}</code> alanı <code>true</code> olan öğrenciler için
          üretilir; diğerleri kuyrukta &quot;rıza bekliyor&quot; durumunda kalır ve hata
          sayılmaz. Aktarımın açık rızaya mı yoksa eğitim sözleşmesinin ifasına mı
          dayanacağı kararı verildiğinde <code>DIJITAL_ROZET_REQUIRE_CONSENT</code>
          {' '}bayrağıyla değiştirilir.
        </p>
      )}

      {ping && data.ping_ok === true && (
        <p className="mt-3 text-sm text-green-700">Bağlantı başarılı: {data.ping_message}</p>
      )}
      {ping && data.ping_ok === false && (
        <p className="mt-3 text-sm text-destructive">Bağlantı hatası: {data.ping_error}</p>
      )}
    </div>
  );
}

// ─── KVKK silme kuyruğu özeti ──────────────────────────────────────────────────

/**
 * KVKK silme kuyruğu sayacı.
 *
 * Silinen öğrenci verilerinin bakanlık tarafındaki rozet kaydı `worker`
 * tarafından temizlenir. Entegrasyon kapalıyken (DIJITAL_ROZET_ENABLED=false)
 * bu worker koşmaz — kuyruk sessizce birikir ve kimse fark etmeden KVKK'nın
 * 30 günlük yanıt süresi aşılabilir. Bu kart o riski görünür kılar; "olsa
 * iyi olur" bir sayaç değil, kapalıyken en çok işe yaradığı için sayfanın
 * her sekmesinde (kurslar/kuyruk fark etmeksizin) görünür tutulur.
 */
function SilmeKuyruguOzeti() {
  const { t } = useLanguage();
  const { data, isLoading, isError, isFetching, refetch } = useDijitalRozetSilmeKuyruguOzeti();

  if (isLoading) {
    return (
      <div className="mt-4 rounded-lg border p-4">
        <LoadingState variant="bars" count={2} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
          <span>{t('admin.dijitalRozet.silmeKuyrugu.error')}</span>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw size={14} className="mr-1" aria-hidden="true" />
          {t('admin.dijitalRozet.silmeKuyrugu.retry')}
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const { pending_count, oldest_age_days, over_threshold, failed_count, threshold_days } = data;

  if (pending_count === 0) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-lg border p-4 text-sm text-muted-foreground">
        <CheckCircle2 size={18} className="shrink-0" aria-hidden="true" />
        <p>
          <span className="font-medium text-foreground">
            {t('admin.dijitalRozet.silmeKuyrugu.emptyTitle')}
          </span>
          {' — '}
          {t('admin.dijitalRozet.silmeKuyrugu.emptyDescription')}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`mt-4 rounded-lg border p-4 ${
        over_threshold ? 'border-destructive/40 bg-destructive/5' : ''
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {over_threshold ? (
            <AlertTriangle size={18} className="shrink-0 text-destructive" aria-hidden="true" />
          ) : (
            <Clock size={18} className="shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <h2 className={`font-medium ${over_threshold ? 'text-destructive' : ''}`}>
            {t('admin.dijitalRozet.silmeKuyrugu.title')}
          </h2>
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge variant={over_threshold ? 'destructive' : 'secondary'}>
            {t('admin.dijitalRozet.silmeKuyrugu.pendingBadge').replace('{{count}}', String(pending_count))}
          </Badge>
          {oldest_age_days != null && (
            <Badge variant="outline">
              {t('admin.dijitalRozet.silmeKuyrugu.oldestAgeBadge').replace('{{days}}', String(oldest_age_days))}
            </Badge>
          )}
          {failed_count > 0 && (
            <Badge variant="destructive">
              {t('admin.dijitalRozet.silmeKuyrugu.failedBadge').replace('{{count}}', String(failed_count))}
            </Badge>
          )}
        </div>
      </div>

      {over_threshold && (
        <p className="mt-3 flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            {t('admin.dijitalRozet.silmeKuyrugu.thresholdWarning')
              .replace('{{days}}', String(oldest_age_days))
              .replace('{{threshold}}', String(threshold_days))}
          </span>
        </p>
      )}

      {!over_threshold && (
        <p className="mt-3 text-xs text-muted-foreground">
          {t('admin.dijitalRozet.silmeKuyrugu.pendingNote')}
        </p>
      )}

      {failed_count > 0 && (
        <p className="mt-2 flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          <XCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            {t('admin.dijitalRozet.silmeKuyrugu.failedWarning').replace('{{count}}', String(failed_count))}
          </span>
        </p>
      )}
    </div>
  );
}

// ─── Kurs listesi ─────────────────────────────────────────────────────────────

function KursListesi() {
  const { data, isLoading } = useDijitalRozetCourses(false);
  const [acikKurs, setAcikKurs] = useState(null);

  if (isLoading) return <LoadingState />;
  const kurslar = data?.courses || [];
  if (kurslar.length === 0) {
    return <EmptyState title="Kurs bulunamadı" description="Rozetlenecek kurs yok." />;
  }

  return (
    <div className="space-y-2">
      {kurslar.map((kurs) => (
        <div key={kurs.course_id} className="rounded-lg border">
          <button
            type="button"
            onClick={() => setAcikKurs(acikKurs === kurs.course_id ? null : kurs.course_id)}
            className="flex w-full items-center justify-between px-4 py-3 text-left"
          >
            <div>
              <div className="font-medium">{kurs.title_tr || kurs.title}</div>
              <div className="text-xs text-muted-foreground">
                {kurs.course_id}
                {kurs.cpd_hours ? ` · ${kurs.cpd_hours} saat` : ' · süre tanımsız'}
                {kurs.dr_synced_at ? ` · son gönderim ${kurs.dr_synced_at.slice(0, 10)}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={kurs.dr_ready ? 'default' : 'outline'}>
                {kurs.dr_ready ? 'Rozet hazır' : 'Yapılandırılmadı'}
              </Badge>
              <span className="text-muted-foreground">{acikKurs === kurs.course_id ? '▲' : '▼'}</span>
            </div>
          </button>

          {acikKurs === kurs.course_id && <KursAyari kurs={kurs} />}
        </div>
      ))}
    </div>
  );
}

function KursAyari({ kurs }) {
  const toast = useToast();
  const [skills, setSkills] = useState(kurs.dr_skills || []);
  const [tip, setTip] = useState(kurs.dr_detail_type ?? 3);
  const [seviye, setSeviye] = useState(kurs.dr_level ?? 5);
  const [kriter, setKriter] = useState(kurs.dr_earning_criteria || '');

  const kaydet = useUpdateCourseRozetSettings();
  const gonder = useSyncCourseDetail();

  const kaydetTikla = async () => {
    try {
      await kaydet.mutateAsync({
        courseId: kurs.course_id,
        data: {
          dr_skills: skills,
          dr_detail_type: Number(tip),
          dr_level: Number(seviye),
          dr_earning_criteria: kriter,
        },
      });
      toast.success('Ayarlar kaydedildi');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const gonderTikla = async () => {
    try {
      const res = await gonder.mutateAsync(kurs.course_id);
      if (res.warning_tr) toast.warning(res.warning_tr);
      else toast.success(res.created ? 'Detail oluşturuldu' : 'Detail güncellendi');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4 border-t px-4 py-4">
      <DijitalRozetSkillPicker value={skills} onChange={setSkills} />

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block font-medium">Kazanım tipi</span>
          <select
            value={tip}
            onChange={(e) => setTip(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            {DETAIL_TIPLERI.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>

        <label className="text-sm">
          <span className="mb-1 block font-medium">Seviye</span>
          <select
            value={seviye}
            onChange={(e) => setSeviye(e.target.value)}
            className="w-full rounded-md border px-3 py-2 text-sm"
          >
            {SEVIYELER.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
      </div>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">Kazanım kriteri</span>
        <textarea
          value={kriter}
          onChange={(e) => setKriter(e.target.value)}
          rows={3}
          placeholder="Ör. Eğitimi tamamlamak ve final sınavını geçmek"
          className="w-full rounded-md border px-3 py-2 text-sm"
        />
        <span className="text-xs text-muted-foreground">
          Doğrulama sayfasında &quot;Earning Criteria&quot; başlığı altında görünür. HTML kullanılabilir.
        </span>
      </label>

      {!kurs.cpd_hours && (
        <p className="rounded-md bg-muted p-3 text-sm">
          Bu kursta <code>cpd_hours</code> tanımlı değil, bu yüzden rozete süre bilgisi
          gönderilmez. Süre göstermek isterseniz kurs kaydına saat bilgisi eklenmeli.
        </p>
      )}

      <div className="flex gap-2">
        <Button type="button" onClick={kaydetTikla} disabled={kaydet.isPending}>
          {kaydet.isPending ? 'Kaydediliyor…' : 'Ayarları kaydet'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={gonderTikla}
          disabled={gonder.isPending || skills.length === 0}
        >
          {gonder.isPending
            ? 'Gönderiliyor…'
            : kurs.dr_detail_id ? 'Detail\'i güncelle' : 'Detail oluştur'}
        </Button>
      </div>

      {kurs.dr_sweeper_warning && (
        <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          Yayınlama görevi uyarısı{kurs.dr_sweeper_warning_at
            ? ` (${kurs.dr_sweeper_warning_at.slice(0, 10)})`
            : ''}: {kurs.dr_sweeper_warning}
        </p>
      )}

      {kurs.dr_detail_id && (
        <p className="text-xs text-muted-foreground">
          Detail ID: {kurs.dr_detail_id}
          {kurs.dr_campaign_id ? ` · Campaign ID: ${kurs.dr_campaign_id}` : ''}
        </p>
      )}
    </div>
  );
}

// ─── Sweeper paneli ───────────────────────────────────────────────────────────

/**
 * Yayınlama görevini panelden tetikler.
 *
 * Sandbox YOK — canlı bakanlık sistemine yazılıyor. Bu yüzden akış iki adımlı:
 * önce kuru koşu (hiçbir çağrı yapılmaz, ne olacağı gösterilir), sonra açık
 * onayla uygulama. Toplu geri dolum buradan değil, CLI'dan yapılır
 * (backend/scripts/dr_sync_pending.py).
 */
function SweeperPaneli() {
  const toast = useToast();
  const [sonuc, setSonuc] = useState(null);
  const sweep = useRunSweep();

  const calistir = async (apply) => {
    try {
      const res = await sweep.mutateAsync({ apply, limit: 25 });
      setSonuc({ ...res, apply });
      if (apply) {
        toast.success(`${res.published} rozet yayınlandı, ${res.failed} başarısız`);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const yayinlanacak = sonuc?.would_publish || 0;

  return (
    <div className="rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-medium">Yayınlama görevi</h3>
          <p className="text-xs text-muted-foreground">
            Rozeti olmayan sertifikaları tarar ve bakanlık platformuna gönderir.
            Önce kuru koşu yapın — test ortamı yoktur, gönderim canlıya gider.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => calistir(false)}
            disabled={sweep.isPending}
          >
            {sweep.isPending ? 'Çalışıyor…' : 'Kuru koşu'}
          </Button>
          {sonuc && !sonuc.apply && yayinlanacak > 0 && (
            <Button
              type="button"
              size="sm"
              onClick={() => calistir(true)}
              disabled={sweep.isPending}
            >
              {yayinlanacak} rozeti yayınla
            </Button>
          )}
        </div>
      </div>

      {sonuc && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">taranan {sonuc.scanned}</Badge>
            <Badge variant={sonuc.apply ? 'default' : 'secondary'}>
              {sonuc.apply ? `yayınlanan ${sonuc.published}` : `yayınlanacak ${sonuc.would_publish}`}
            </Badge>
            {sonuc.failed > 0 && <Badge variant="destructive">başarısız {sonuc.failed}</Badge>}
            {sonuc.skipped > 0 && <Badge variant="outline">atlandı {sonuc.skipped}</Badge>}
            {sonuc.awaiting_consent > 0 && (
              <Badge variant="outline">rıza bekleyen {sonuc.awaiting_consent}</Badge>
            )}
          </div>

          {(sonuc.warnings || []).map((u, i) => (
            <p
              key={`${u.code}-${i}`}
              className={`rounded-md p-2 text-sm ${
                u.code === 'verification_link_lost'
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-muted'
              }`}
            >
              {u.message_tr}
            </p>
          ))}

          {sonuc.aborted && (
            <p className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
              Koşu yarıda durdu: {sonuc.aborted}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Rozet kuyruğu ────────────────────────────────────────────────────────────

function RozetKuyrugu() {
  const toast = useToast();
  const [durum, setDurum] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const { data, isLoading } = useDijitalRozetCertificates(durum, sayfa, 20);
  const yenidenDene = useRetryCertificateRozet();

  const denemeTikla = async (certId) => {
    try {
      await yenidenDene.mutateAsync(certId);
      toast.success('Kuyruğa alındı');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4">
      <SweeperPaneli />

      <div className="flex flex-wrap gap-2">
        {KUYRUK_DURUMLARI.map((d) => (
          <Button
            key={d.value || 'tumu'}
            type="button"
            size="sm"
            variant={durum === d.value ? 'default' : 'outline'}
            onClick={() => { setDurum(d.value); setSayfa(1); }}
          >
            {d.label}
          </Button>
        ))}
      </div>

      {isLoading && <LoadingState />}

      {!isLoading && (data?.items || []).length === 0 && (
        <EmptyState
          title="Kayıt yok"
          description="Bu durumda sertifika bulunmuyor. Rozet üretimi henüz başlamamış olabilir."
        />
      )}

      {(data?.items || []).map((c) => (
        <div key={c.certificate_id} className="flex items-center justify-between rounded-lg border px-4 py-3">
          <div className="min-w-0">
            <div className="truncate font-medium">{c.user_name}</div>
            <div className="truncate text-xs text-muted-foreground">
              {c.course_title_tr || c.course_title} · {(c.issued_at || '').slice(0, 10)}
              {c.dr_attempts ? ` · ${c.dr_attempts} deneme` : ''}
            </div>
            {c.dr_last_error && (
              <div className="mt-1 truncate text-xs text-destructive">{c.dr_last_error}</div>
            )}
            {c.dr_skip_reason && !c.dr_last_error && (
              <div className="mt-1 text-xs text-muted-foreground">
                {ATLAMA_SEBEBI[c.dr_skip_reason] || c.dr_skip_reason}
              </div>
            )}
            {c.dr_orphan_credential_id && (
              <div className="mt-1 text-xs text-destructive">
                Bakanlık tarafında kredensiyel oluştu ({c.dr_orphan_credential_id}) ancak
                doğrulama linki kaydedilemedi. Yeniden gönderim ikinci bir rozet üretir —
                önce uzaktaki kredensiyel silinmelidir.
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {c.dr_verification_link && (
              <a
                href={c.dr_verification_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm underline"
              >
                Doğrula
              </a>
            )}
            <Badge variant={DURUM_RENGI[c.dr_status] || 'outline'}>
              {DURUM_ETIKETI[c.dr_status] || c.dr_status || 'üretilmedi'}
            </Badge>
            {c.dr_status === 'failed' && !c.dr_orphan_credential_id && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => denemeTikla(c.certificate_id)}
                disabled={yenidenDene.isPending}
              >
                Yeniden dene
              </Button>
            )}
          </div>
        </div>
      ))}

      {(data?.total || 0) > 20 && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={sayfa === 1}
            onClick={() => setSayfa((s) => s - 1)}
          >
            Önceki
          </Button>
          <span className="text-sm text-muted-foreground">
            Sayfa {sayfa} / {Math.ceil((data?.total || 0) / 20)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={sayfa >= Math.ceil((data?.total || 0) / 20)}
            onClick={() => setSayfa((s) => s + 1)}
          >
            Sonraki
          </Button>
        </div>
      )}
    </div>
  );
}
